/**
 * Scheduler for SNES Composition Engine
 *
 * Scans a project for events within a time window and generates
 * audio commands with voice assignments.
 */

import type { Project, Note } from '../model/types.js';
import { beatsToSeconds, beats } from '../model/types.js';
import { VoiceAllocator } from './VoiceAllocator.js';

// =============================================================================
// Commands
// =============================================================================

/**
 * Base command interface.
 */
interface BaseCommand {
  readonly time: number; // AudioContext time in seconds
  readonly beat: number; // Time in beats for debugging
}

/**
 * Note on command.
 */
export interface NoteOnCommand extends BaseCommand {
  readonly type: 'noteOn';
  readonly voice: number;
  readonly instrumentId: string;
  readonly pitch: number;
  readonly velocity: number;
  readonly noteId: string;
}

/**
 * Note off command.
 */
export interface NoteOffCommand extends BaseCommand {
  readonly type: 'noteOff';
  readonly voice: number;
  readonly noteId: string;
}

/**
 * All command types.
 */
export type SchedulerCommand = NoteOnCommand | NoteOffCommand;

// =============================================================================
// Scheduled Note Tracking
// =============================================================================

/**
 * A scheduled note event (before voice allocation).
 */
export interface ScheduledNote {
  readonly noteId: string;
  readonly trackId: string;
  readonly instrumentId: string;
  readonly pitch: number;
  readonly velocity: number;
  readonly startBeat: number;
  readonly endBeat: number;
}

/**
 * Unique ID generator for notes.
 */
let noteIdCounter = 0;
export function generateNoteId(): string {
  return `note_${++noteIdCounter}`;
}

/**
 * Reset note ID counter (for testing).
 */
export function resetNoteIdCounter(): void {
  noteIdCounter = 0;
}

// =============================================================================
// Scheduler
// =============================================================================

/**
 * Configuration for the scheduler.
 */
export interface SchedulerConfig {
  readonly tempo: number;
  readonly startTimeSeconds: number; // AudioContext time when playback started
  readonly startBeat: number; // Beat position where playback started
}

/**
 * Scheduler that scans a project and generates commands.
 *
 * Design:
 * - Scans project for notes in a given beat range
 * - Allocates voices via VoiceAllocator
 * - Generates NoteOn/NoteOff commands with precise timing
 * - Handles project changes by re-scanning from current position
 */
export class Scheduler {
  private readonly voiceAllocator: VoiceAllocator;
  private scheduledNotes: Map<string, ScheduledNote> = new Map();
  private lastScheduledBeat: number = 0;

  constructor(voiceCount: number = 8) {
    this.voiceAllocator = new VoiceAllocator(voiceCount);
  }

  /**
   * Get commands for all notes starting in the given beat range.
   *
   * @param project The project to scan
   * @param config Timing configuration
   * @param fromBeat Start of range (inclusive)
   * @param toBeat End of range (exclusive)
   * @returns Array of commands to execute
   */
  getCommandsInRange(
    project: Project,
    config: SchedulerConfig,
    fromBeat: number,
    toBeat: number
  ): SchedulerCommand[] {
    const commands: SchedulerCommand[] = [];

    // Collect all note events in range
    const noteStarts: ScheduledNote[] = [];
    const noteEnds: Array<{ noteId: string; beat: number }> = [];

    // Check for note-offs of already scheduled notes
    for (const [noteId, scheduled] of this.scheduledNotes) {
      if (scheduled.endBeat >= fromBeat && scheduled.endBeat < toBeat) {
        noteEnds.push({ noteId, beat: scheduled.endBeat });
      }
    }

    // Scan project for new notes starting in range
    for (const track of project.tracks) {
      if (track.mute) continue;

      for (const clip of track.clips) {
        // clip.start and clip.duration are already in beats
        const clipStartBeat = clip.start;
        const clipEndBeat = clipStartBeat + clip.duration;

        // Skip clips entirely outside our range
        if (clipEndBeat <= fromBeat || clipStartBeat >= toBeat) continue;

        for (const note of clip.notes) {
          const noteStartBeat = clipStartBeat + note.time;
          const noteEndBeat = noteStartBeat + note.duration;

          // Note starts in range?
          if (noteStartBeat >= fromBeat && noteStartBeat < toBeat) {
            const noteId = generateNoteId();
            const scheduled: ScheduledNote = {
              noteId,
              trackId: track.id,
              instrumentId: track.instrumentId,
              pitch: note.pitch,
              velocity: note.velocity,
              startBeat: noteStartBeat,
              endBeat: noteEndBeat,
            };
            noteStarts.push(scheduled);
            this.scheduledNotes.set(noteId, scheduled);

            // Also check if note ends in this range
            if (noteEndBeat >= fromBeat && noteEndBeat < toBeat) {
              noteEnds.push({ noteId, beat: noteEndBeat });
            }
          }
        }
      }
    }

    // Generate note-on commands
    for (const scheduled of noteStarts) {
      const audioTime = this.beatToAudioTime(scheduled.startBeat, config);
      const allocation = this.voiceAllocator.allocate(
        scheduled.instrumentId,
        scheduled.pitch,
        scheduled.noteId,
        audioTime
      );

      commands.push({
        type: 'noteOn',
        time: audioTime,
        beat: scheduled.startBeat,
        voice: allocation.voiceIndex,
        instrumentId: scheduled.instrumentId,
        pitch: scheduled.pitch,
        velocity: scheduled.velocity,
        noteId: scheduled.noteId,
      });
    }

    // Generate note-off commands
    for (const { noteId, beat } of noteEnds) {
      const voiceIndex = this.voiceAllocator.findVoiceByNoteId(noteId);
      if (voiceIndex !== -1) {
        const audioTime = this.beatToAudioTime(beat, config);
        commands.push({
          type: 'noteOff',
          time: audioTime,
          beat,
          voice: voiceIndex,
          noteId,
        });
        this.voiceAllocator.release(noteId);
        this.scheduledNotes.delete(noteId);
      }
    }

    // Sort by time, note-offs before note-ons at same time
    commands.sort((a, b) => {
      if (a.time !== b.time) return a.time - b.time;
      return a.type === 'noteOff' ? -1 : 1;
    });

    this.lastScheduledBeat = toBeat;
    return commands;
  }

  /**
   * Handle a project change during playback.
   * Clears scheduled state and prepares to rescan.
   *
   * @returns Note IDs that should have their voices released
   */
  handleProjectChange(): string[] {
    const activeNoteIds = Array.from(this.scheduledNotes.keys());
    this.scheduledNotes.clear();
    // Note: We don't reset the voice allocator - the caller should
    // send note-offs for the returned IDs and then those voices
    // will naturally become available
    return activeNoteIds;
  }

  /**
   * Get all currently scheduled notes.
   */
  getScheduledNotes(): ScheduledNote[] {
    return Array.from(this.scheduledNotes.values());
  }

  /**
   * Get the voice allocator (for inspection/testing).
   */
  getVoiceAllocator(): VoiceAllocator {
    return this.voiceAllocator;
  }

  /**
   * Reset the scheduler to initial state.
   */
  reset(): void {
    this.scheduledNotes.clear();
    this.voiceAllocator.reset();
    this.lastScheduledBeat = 0;
  }

  /**
   * Get the last beat position that was scheduled.
   */
  getLastScheduledBeat(): number {
    return this.lastScheduledBeat;
  }

  /**
   * Convert a beat position to AudioContext time.
   */
  private beatToAudioTime(beat: number, config: SchedulerConfig): number {
    const beatsFromStart = beat - config.startBeat;
    const secondsFromStart = beatsToSeconds(beats(beatsFromStart), config.tempo);
    return config.startTimeSeconds + secondsFromStart;
  }
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Calculate the beat position at a given AudioContext time.
 */
export function audioTimeToBeat(audioTime: number, config: SchedulerConfig): number {
  const secondsFromStart = audioTime - config.startTimeSeconds;
  const beatsFromStart = (secondsFromStart * config.tempo) / 60;
  return config.startBeat + beatsFromStart;
}

/**
 * Get all notes that would be playing at a given beat.
 */
export function getActiveNotesAtBeat(
  project: Project,
  beat: number
): Array<{ trackId: string; note: Note; clipStartBeat: number }> {
  const active: Array<{ trackId: string; note: Note; clipStartBeat: number }> = [];

  for (const track of project.tracks) {
    if (track.mute) continue;

    for (const clip of track.clips) {
      // clip.start and clip.duration are already in beats
      const clipStartBeat = clip.start;
      const clipEndBeat = clipStartBeat + clip.duration;

      if (beat < clipStartBeat || beat >= clipEndBeat) continue;

      for (const note of clip.notes) {
        const noteStartBeat = clipStartBeat + note.time;
        const noteEndBeat = noteStartBeat + note.duration;

        if (beat >= noteStartBeat && beat < noteEndBeat) {
          active.push({ trackId: track.id, note, clipStartBeat });
        }
      }
    }
  }

  return active;
}
